import { useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext'
import { supabase } from '../lib/supabase'

const FREE_GENERATION_LIMIT = 1
const FREE_MONTHLY_LIMIT = 5 // Free users get 5 generations per month
const STORAGE_KEY = 'toefetti_anonymous_generations'
const CREDIT_COST_PER_GENERATION = 1

interface GenerationUsage {
  count: number
  lastReset: string
}

export const useGenerationLimit = () => {
  const { user, profile } = useAuth()
  const [anonymousGenerations, setAnonymousGenerations] = useState<number>(0)
  const [loading, setLoading] = useState(true)
  const [creditBalance, setCreditBalance] = useState<number>(0)
  const [subscriptionStatus, setSubscriptionStatus] = useState<string>('free')
  const [monthlyGenerations, setMonthlyGenerations] = useState<number>(0)

  useEffect(() => {
    if (user && profile) {
      // Load user's credit balance, subscription status, and monthly generation count
      loadUserData()
      return
    }

    if (!user) {
      // Load anonymous usage from localStorage
      loadAnonymousUsage()
    }
  }, [user, profile])

  const loadUserData = async () => {
    if (!user) return

    try {
      setLoading(true)
      
      // Get credit balance from profile or database
      const currentCredits = (profile as any)?.credit_balance || 0
      setCreditBalance(currentCredits)
      
      // Get subscription status
      const subStatus = profile.subscription_tier || 'free'
      setSubscriptionStatus(subStatus)
      
      // Get monthly generation count
      const startOfMonth = new Date()
      startOfMonth.setDate(1)
      startOfMonth.setHours(0, 0, 0, 0)
      
      const { data: monthlyData, error } = await supabase
        .from('images')
        .select('id')
        .eq('user_id', user.id)
        .gte('created_at', startOfMonth.toISOString())
      
      if (error) {
        console.error('Error fetching monthly generations:', error)
        setMonthlyGenerations(0)
      } else {
        setMonthlyGenerations(monthlyData?.length || 0)
      }
      
    } catch (error) {
      console.error('Error loading user data:', error)
      setCreditBalance(0)
      setSubscriptionStatus('free')
      setMonthlyGenerations(0)
    } finally {
      setLoading(false)
    }
  }

  const loadAnonymousUsage = () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const usage: GenerationUsage = JSON.parse(stored)
        
        // Check if we need to reset (daily reset)
        const lastReset = new Date(usage.lastReset)
        const now = new Date()
        const daysSinceReset = Math.floor((now.getTime() - lastReset.getTime()) / (1000 * 60 * 60 * 24))
        
        if (daysSinceReset >= 1) {
          // Reset daily limit
          const newUsage: GenerationUsage = {
            count: 0,
            lastReset: now.toISOString()
          }
          localStorage.setItem(STORAGE_KEY, JSON.stringify(newUsage))
          setAnonymousGenerations(0)
        } else {
          setAnonymousGenerations(usage.count)
        }
      } else {
        // First time user
        const newUsage: GenerationUsage = {
          count: 0,
          lastReset: new Date().toISOString()
        }
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newUsage))
        setAnonymousGenerations(0)
      }
    } catch (error) {
      console.error('Error loading anonymous usage:', error)
      setAnonymousGenerations(0)
    }
    setLoading(false)
  }

  const consumeCredits = async (amount: number = CREDIT_COST_PER_GENERATION): Promise<boolean> => {
    if (!user || !profile) {
      console.error('Cannot consume credits: user not logged in')
      return false
    }

    try {
      // Check if user has enough credits
      if (creditBalance < amount) {
        console.error('Insufficient credits')
        return false
      }

      // Consume credits via Supabase function
      const { data, error } = await supabase.rpc('consume_user_credits', {
        user_id: user.id,
        amount: amount,
        description: `Image generation (${amount} credit${amount > 1 ? 's' : ''})`
      })

      if (error) {
        console.error('Error consuming credits:', error)
        return false
      }

      // Update local credit balance
      setCreditBalance(data.new_balance)
      
      return true
    } catch (error) {
      console.error('Error consuming credits:', error)
      return false
    }
  }

  const incrementAnonymousUsage = () => {
    if (user) return // Logged in users don't use anonymous tracking

    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      const usage: GenerationUsage = stored 
        ? JSON.parse(stored)
        : { count: 0, lastReset: new Date().toISOString() }

      const newUsage: GenerationUsage = {
        count: usage.count + 1,
        lastReset: usage.lastReset
      }

      localStorage.setItem(STORAGE_KEY, JSON.stringify(newUsage))
      setAnonymousGenerations(newUsage.count)
    } catch (error) {
      console.error('Error incrementing anonymous usage:', error)
    }
  }

  const incrementMonthlyUsage = () => {
    if (user) {
      setMonthlyGenerations(prev => prev + 1)
    }
  }

  const getRemainingGenerations = (): number => {
    if (!user) {
      // Anonymous users have daily limits
      return Math.max(0, FREE_GENERATION_LIMIT - anonymousGenerations)
    }

    // Premium subscribers have unlimited generations
    if (subscriptionStatus === 'premium' || subscriptionStatus === 'pro') {
      return -1 // Unlimited
    }

    // Free users get monthly limit + credits
    const monthlyRemaining = Math.max(0, FREE_MONTHLY_LIMIT - monthlyGenerations)
    return monthlyRemaining + creditBalance
  }

  const canGenerate = (): boolean => {
    if (!user) {
      // Anonymous users can generate within daily limit
      return anonymousGenerations < FREE_GENERATION_LIMIT
    }

    // Premium subscribers can always generate
    if (subscriptionStatus === 'premium' || subscriptionStatus === 'pro') {
      return true
    }

    // Free users get 5 free generations per month, then need credits
    const monthlyRemaining = Math.max(0, FREE_MONTHLY_LIMIT - monthlyGenerations)
    return monthlyRemaining > 0 || creditBalance >= CREDIT_COST_PER_GENERATION
  }

  const isAtLimit = (): boolean => {
    if (!user) {
      return anonymousGenerations >= FREE_GENERATION_LIMIT
    }

    // Premium subscribers are never at limit
    if (subscriptionStatus === 'premium' || subscriptionStatus === 'pro') {
      return false
    }

    // Free users are at limit when they've used monthly limit and have no credits
    const monthlyRemaining = Math.max(0, FREE_MONTHLY_LIMIT - monthlyGenerations)
    return monthlyRemaining === 0 && creditBalance < CREDIT_COST_PER_GENERATION
  }

  const getGenerationType = (): 'anonymous' | 'credits' | 'subscription' | 'free_monthly' => {
    if (!user) return 'anonymous'
    if (subscriptionStatus === 'premium' || subscriptionStatus === 'pro') return 'subscription'
    
    // Check if user still has free monthly generations
    const monthlyRemaining = Math.max(0, FREE_MONTHLY_LIMIT - monthlyGenerations)
    if (monthlyRemaining > 0) return 'free_monthly'
    
    return 'credits'
  }

  const getGenerationCost = (): number => {
    const type = getGenerationType()
    if (type === 'subscription' || type === 'free_monthly') return 0
    if (type === 'credits') return CREDIT_COST_PER_GENERATION
    return 0 // Anonymous is free (within limits)
  }

  return {
    loading,
    anonymousGenerations,
    creditBalance,
    subscriptionStatus,
    monthlyGenerations,
    remainingGenerations: getRemainingGenerations(),
    canGenerate: canGenerate(),
    isAtLimit: isAtLimit(),
    incrementAnonymousUsage,
    incrementMonthlyUsage,
    consumeCredits,
    getGenerationType,
    getGenerationCost,
    freeLimit: FREE_GENERATION_LIMIT,
    freeMonthlyLimit: FREE_MONTHLY_LIMIT,
    creditCostPerGeneration: CREDIT_COST_PER_GENERATION
  }
} 